<template>
  <div>
    <Head title="Clientes" />

    <!-- Header & Actions -->
    <div class="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
      <h1 class="text-3xl font-bold text-gray-800">Clientes</h1>
      
      <div class="flex flex-wrap items-center gap-3">
        <!-- Export Dropdown Button -->
        <div ref="exportDropdownRef" class="relative">
          <button 
            type="button"
            class="px-4 py-2 bg-white border border-gray-300 hover:border-gray-400 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors font-medium shadow-sm text-sm"
            @click.stop="toggleExportDropdown"
          >
            <i class="fa fa-download text-gray-500" />
            <span>Exportar</span>
            <i class="fa fa-chevron-down text-[10px] text-gray-400 transition-transform duration-200" :class="{'rotate-180': isExportOpen}" />
          </button>

          <!-- Dropdown Menu -->
          <div 
            v-if="isExportOpen" 
            class="absolute top-full right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 z-50 py-1.5 animate-in fade-in zoom-in-95 duration-150 overflow-hidden"
          >
            <a 
              href="/clientsReports/excel" 
              target="_blank"
              class="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors"
              @click="isExportOpen = false"
            >
              <div class="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center shrink-0">
                <i class="fa fa-file-excel text-green-600 text-sm" />
              </div>
              <div class="text-left">
                <div class="font-bold text-xs text-gray-900">Exportar a Excel</div>
                <div class="text-[10px] text-gray-400">Descargar archivo .xlsx</div>
              </div>
            </a>

            <a 
              href="/clientsReports/pdf" 
              target="_blank"
              class="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors border-t border-gray-50"
              @click="isExportOpen = false"
            >
              <div class="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
                <i class="fa fa-file-pdf text-red-600 text-sm" />
              </div>
              <div class="text-left">
                <div class="font-bold text-xs text-gray-900">Exportar a PDF</div>
                <div class="text-[10px] text-gray-400">Documento imprimible .pdf</div>
              </div>
            </a>
          </div>
        </div>

        <!-- Import Button (Abre Modal) -->
        <button 
          type="button"
          class="px-4 py-2 bg-white border border-gray-300 hover:border-gray-400 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors font-medium shadow-sm text-sm"
          @click="openImportModal"
        >
          <i class="fa fa-upload text-gray-500" />
          Importar
        </button>

        <!-- Create Button -->
        <Link 
          href="/clients/create" 
          class="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center gap-2 transition-colors font-medium shadow-sm text-sm"
        >
          <i class="fa fa-plus" />
          Crear Cliente
        </Link>
      </div>
    </div>

    <!-- Search Tool Bar (Sin botón de filtrar) -->
    <div class="mb-6">
      <div class="relative w-full">
        <div class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
          <icon name="search" class="w-4 h-4 text-gray-400" />
        </div>
        <input 
          v-model="form.search" 
          type="text" 
          class="block w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-all shadow-sm text-sm" 
          placeholder="Buscar cliente por nombre, empresa o documento..."
        />
      </div>
    </div>

    <!-- Content Table -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <!-- Mobile View -->
      <div class="sm:hidden divide-y divide-gray-100">
        <div v-for="client in clients.data" :key="client.id" class="p-4 hover:bg-gray-50 transition-colors" :class="{'bg-red-50': client.balance > 0}">
          <Link :href="`/clients/${client.id}`" class="space-y-3 block">
            <div class="flex justify-between items-start">
              <div>
                <h3 class="font-bold text-gray-900">{{ client.companyName || client.name }}</h3>
                <p v-if="client.companyName" class="text-sm text-gray-500">{{ client.name }}</p>
              </div>
              <span v-if="client.balance > 0" class="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full font-bold">
                Deuda
              </span>
            </div>
            
            <div class="text-sm text-gray-600 space-y-1">
              <div class="flex items-center gap-2">
                <i class="fa fa-id-card text-gray-400 w-4" />
                <span>{{ client.identification }}</span>
              </div>
              <div class="flex items-center gap-2">
                <i class="fa fa-envelope text-gray-400 w-4" />
                <span>{{ client.email }}</span>
              </div>
            </div>

            <div class="pt-2 flex justify-between items-center border-t border-gray-100 mt-2">
              <span class="font-bold text-lg" :class="client.balance > 0 ? 'text-red-600' : 'text-gray-900'">
                {{ formatCurrency(client.balance) }}
              </span>
              <i class="fa fa-chevron-right text-gray-300" />
            </div>
          </Link>
        </div>
      </div>

      <!-- Desktop View -->
      <div class="hidden sm:block overflow-x-auto">
        <table class="w-full whitespace-nowrap">
          <thead>
            <tr class="bg-gray-50/50 text-left border-b border-gray-100">
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Empresa</th>
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Nombre</th>
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Alias o Cédula</th>
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Correo electrónico</th>
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Dirección</th>
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider text-right">Adeudado</th>
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Teléfono</th>
              <th class="py-4 px-6" />
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr 
              v-for="client in clients.data" 
              :key="client.id" 
              class="group hover:bg-gray-50 transition-colors"
              :class="{'bg-red-50 hover:bg-red-100': client.balance > 0}"
            >
              <td class="px-6 py-4">
                <Link :href="`/clients/${client.id}`" class="block">
                  <span class="font-medium text-gray-900">{{ client.companyName || '-' }}</span>
                </Link>
              </td>
              <td class="px-6 py-4">
                <Link :href="`/clients/${client.id}`" class="flex items-center gap-2">
                  <span class="text-gray-700">{{ client.name }}</span>
                  <icon v-if="client.deleted_at" name="trash" class="w-3 h-3 text-red-400" title="Eliminado" />
                </Link>
              </td>
              <td class="px-6 py-4 text-gray-600 text-sm">{{ client.identification }}</td>
              <td class="px-6 py-4 text-gray-600 text-sm">{{ client.email }}</td>
              <td class="px-6 py-4 text-gray-600 text-sm max-w-xs truncate" :title="client.address">{{ client.address }}</td>
              <td class="px-6 py-4 text-right font-medium" :class="client.balance > 0 ? 'text-red-600' : 'text-gray-900'">
                {{ formatCurrency(client.balance) }}
              </td>
              <td class="px-6 py-4 text-gray-600 text-sm">{{ client.phone }}</td>
              <td class="px-6 py-4 text-right">
                <div class="flex items-center justify-end gap-2">
                  <button 
                    class="text-gray-400 hover:text-red-600 transition-colors p-1" 
                    title="Eliminar cliente"
                    @click.stop="deleteClient(client)"
                  >
                    <i class="fa fa-trash" />
                  </button>
                  <Link :href="`/clients/${client.id}`" class="text-gray-400 hover:text-orange-500 transition-colors p-1">
                    <i class="fa fa-chevron-right" />
                  </Link>
                </div>
              </td>
            </tr>

            <!-- Empty State -->
            <tr v-if="clients.data.length === 0">
              <td colspan="8" class="py-20 text-center">
                <div class="flex flex-col items-center justify-center text-gray-400">
                  <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <icon name="users" class="w-8 h-8 opacity-50" />
                  </div>
                  <h3 class="text-lg font-medium text-gray-900 mb-1">No hay clientes disponibles</h3>
                  <p class="text-gray-500 text-sm max-w-sm">
                    Empieza por crear un nuevo cliente en la plataforma o importa una lista existente.
                  </p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pagination -->
    <div class="mt-6 flex justify-center">
      <pagination v-if="clients.links" :links="clients.links" />
    </div>

    <!-- Modal de Importación -->
    <div 
      v-if="isImportModalOpen" 
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm"
      @click.self="closeImportModal"
    >
      <div class="bg-white rounded-2xl shadow-2xl max-w-4xl w-full overflow-hidden flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-200">
        <!-- Header del Modal -->
        <div class="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center text-lg">
              <i class="fa fa-file-import" />
            </div>
            <div>
              <h3 class="text-base sm:text-lg font-bold text-gray-900">Importación Masiva de Clientes</h3>
              <p class="text-xs text-gray-500">
                {{ importStep === 'upload' ? 'Paso 1: Descarga la plantilla y sube tu archivo' : 'Paso 2: Previsualiza los clientes y emite la importación' }}
              </p>
            </div>
          </div>
          <button 
            class="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-400 hover:text-gray-600 flex items-center justify-center transition-colors"
            @click="closeImportModal"
          >
            <i class="fa fa-times text-sm" />
          </button>
        </div>

        <!-- Contenido: Paso 1 (Subida & Plantilla) -->
        <div v-if="importStep === 'upload'" class="p-6 overflow-y-auto space-y-6 flex-1">
          <!-- Banner Descargar Plantilla -->
          <div class="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200/80 rounded-2xl p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div class="flex items-center gap-4">
              <div class="w-12 h-12 rounded-xl bg-white shadow-sm border border-orange-100 flex items-center justify-center shrink-0">
                <i class="fa fa-file-excel text-green-600 text-2xl" />
              </div>
              <div>
                <h4 class="text-sm font-bold text-gray-900">Plantilla Oficial de Clientes</h4>
                <p class="text-xs text-gray-600 mt-0.5 max-w-md">
                  Descarga el formato Excel listo con los encabezados requeridos y datos de ejemplo para garantizar una importación sin errores.
                </p>
              </div>
            </div>
            <a 
              href="/clients/template" 
              class="px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-xs font-bold uppercase tracking-wider flex items-center gap-2 shrink-0 transition-colors shadow-sm shadow-orange-200 active:scale-95"
            >
              <i class="fa fa-download" />
              Descargar Plantilla
            </a>
          </div>

          <!-- Drag and Drop / Selector de Archivo -->
          <div>
            <label class="block text-xs font-bold uppercase text-gray-500 tracking-wider mb-2">
              Sube tu archivo completado (.xlsx, .xls, .csv)
            </label>
            <div 
              :class="[
                'border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all flex flex-col items-center justify-center gap-3',
                isDragging ? 'border-orange-500 bg-orange-50/50' : 'border-gray-200 hover:border-orange-400 hover:bg-gray-50/50 bg-white'
              ]"
              @dragover.prevent="isDragging = true"
              @dragleave.prevent="isDragging = false"
              @drop.prevent="handleFileDrop"
              @click="$refs.fileInput.click()"
            >
              <input 
                ref="fileInput" 
                type="file" 
                class="hidden" 
                accept=".xlsx,.xls,.csv" 
                @change="handleFileSelected" 
              />
              
              <div v-if="isPreviewLoading" class="flex flex-col items-center gap-2 py-4">
                <i class="fa fa-spinner fa-spin text-3xl text-orange-500" />
                <p class="text-sm font-bold text-gray-700">Analizando archivo...</p>
                <p class="text-xs text-gray-400">Leyendo y validando columnas de clientes</p>
              </div>

              <template v-else>
                <div class="w-14 h-14 rounded-full bg-orange-50 text-orange-500 flex items-center justify-center text-2xl">
                  <i class="fa fa-cloud-upload-alt" />
                </div>
                <div>
                  <p class="text-sm font-bold text-gray-800">
                    Haz clic aquí para seleccionar tu archivo o arrástralo hasta esta zona
                  </p>
                  <p class="text-xs text-gray-400 mt-1">
                    Formatos compatibles: Microsoft Excel (.xlsx, .xls) o CSV (.csv)
                  </p>
                </div>
              </template>
            </div>
            
            <p v-if="previewError" class="text-xs text-red-600 mt-3 font-semibold flex items-center gap-1.5 bg-red-50 p-2.5 rounded-lg border border-red-100">
              <i class="fa fa-exclamation-circle" />
              {{ previewError }}
            </p>
          </div>
        </div>

        <!-- Contenido: Paso 2 (Previsualización) -->
        <div v-else-if="importStep === 'preview'" class="p-6 overflow-y-auto space-y-4 flex-1 flex flex-col">
          <!-- Resumen de detección -->
          <div class="bg-gray-50 border border-gray-200 rounded-xl p-4 flex flex-wrap items-center justify-between gap-4">
            <div class="flex items-center gap-3">
              <div class="w-9 h-9 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center text-sm font-bold">
                <i class="fa fa-table" />
              </div>
              <div>
                <div class="text-sm font-bold text-gray-900">
                  Previsualización: {{ previewData.total }} clientes listos para importar
                </div>
                <div class="text-xs text-gray-500">
                  Archivo cargado: <span class="font-semibold text-gray-700">{{ selectedFileName }}</span>
                </div>
              </div>
            </div>

            <div class="flex items-center gap-2">
              <span class="px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700 border border-green-200">
                <i class="fa fa-plus-circle mr-1" /> {{ previewData.newCount }} Nuevos
              </span>
              <span class="px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700 border border-blue-200">
                <i class="fa fa-sync-alt mr-1" /> {{ previewData.updateCount }} A actualizar
              </span>
            </div>
          </div>

          <!-- Tabla de Previsualización -->
          <div class="border border-gray-200 rounded-xl overflow-hidden flex-1 flex flex-col min-h-[260px] max-h-[380px]">
            <div class="overflow-x-auto overflow-y-auto flex-1">
              <table class="w-full text-left text-xs whitespace-nowrap">
                <thead class="bg-gray-100 text-gray-600 uppercase font-bold sticky top-0 z-10 border-b border-gray-200">
                  <tr>
                    <th class="py-2.5 px-3 w-10">#</th>
                    <th class="py-2.5 px-3">Empresa / Razón Social</th>
                    <th class="py-2.5 px-3">Contacto</th>
                    <th class="py-2.5 px-3">Cédula / RIF</th>
                    <th class="py-2.5 px-3">Correo</th>
                    <th class="py-2.5 px-3">Teléfono</th>
                    <th class="py-2.5 px-3">Dirección</th>
                    <th class="py-2.5 px-3 text-center">Tipo</th>
                    <th class="py-2.5 px-3 text-center">Acción</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-100">
                  <tr v-for="row in previewData.rows" :key="row.index" class="hover:bg-gray-50/70">
                    <td class="py-2 px-3 text-gray-400 font-mono">{{ row.index }}</td>
                    <td class="py-2 px-3 font-semibold text-gray-900">{{ row.companyName }}</td>
                    <td class="py-2 px-3 text-gray-700">{{ row.name }}</td>
                    <td class="py-2 px-3 font-mono text-gray-600">{{ row.identification }}</td>
                    <td class="py-2 px-3 text-gray-600">{{ row.email }}</td>
                    <td class="py-2 px-3 text-gray-600">{{ row.phone }}</td>
                    <td class="py-2 px-3 text-gray-500 max-w-xs truncate" :title="row.address">{{ row.address }}</td>
                    <td class="py-2 px-3 text-center">{{ row.typeClient }}</td>
                    <td class="py-2 px-3 text-center">
                      <span 
                        v-if="row.action === 'new'" 
                        class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-50 text-green-700 border border-green-200"
                      >
                        Nuevo
                      </span>
                      <span 
                        v-else 
                        class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200"
                      >
                        Actualizar
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <p v-if="previewError" class="text-xs text-red-600 font-semibold flex items-center gap-1.5 bg-red-50 p-2.5 rounded-lg border border-red-100">
            <i class="fa fa-exclamation-circle" />
            {{ previewError }}
          </p>
        </div>

        <!-- Footer del Modal -->
        <div class="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
          <button 
            type="button"
            class="px-4 py-2 border border-gray-300 hover:bg-gray-100 text-gray-700 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors"
            @click="closeImportModal"
          >
            Cancelar
          </button>

          <div class="flex items-center gap-3">
            <button 
              v-if="importStep === 'preview'"
              type="button"
              :disabled="isImporting"
              class="px-4 py-2 bg-white border border-gray-300 hover:bg-gray-100 text-gray-700 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-50"
              @click="resetImportFile"
            >
              <i class="fa fa-arrow-left mr-1" />
              Cambiar Archivo
            </button>

            <button 
              v-if="importStep === 'preview'"
              type="button"
              :disabled="isImporting || previewData.total === 0"
              class="px-6 py-2.5 bg-orange-600 hover:bg-orange-700 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-md shadow-orange-300 flex items-center gap-2 active:scale-95 disabled:opacity-50"
              @click="submitImport"
            >
              <i v-if="isImporting" class="fa fa-spinner fa-spin" />
              <i v-else class="fa fa-rocket" />
              <span>{{ isImporting ? 'Emitiendo Importación...' : 'Emitir Importación' }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'
import SearchFilter from '@/Shared/SearchFilter'
import axios from 'axios'
import Swal from 'sweetalert2'

export default {
  name: 'ClientsIndex',

  components: {
    Head,
    Icon,
    Link,
    Pagination,
    SearchFilter,
  },

  layout: Layout,

  props: {
    filters: Object,
    clients: Object,
    account: Object,
    csrf: {
      type: String,
      required: false,
    },
  },

  data() {
    return {
      form: {
        search: this.filters.search,
        trashed: this.filters.trashed,
      },
      isExportOpen: false,
      isImportModalOpen: false,
      importStep: 'upload', // 'upload' | 'preview'
      selectedFile: null,
      selectedFileName: '',
      isDragging: false,
      isPreviewLoading: false,
      isImporting: false,
      previewError: '',
      previewData: {
        total: 0,
        newCount: 0,
        updateCount: 0,
        rows: [],
      },
    }
  },

  mounted() {
    document.addEventListener('click', this.handleOutsideClick)
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick)
  },

  watch: {
    form: {
      deep: true,
      handler: throttle(function() {
        this.$inertia.get('/clients', pickBy(this.form), { 
          preserveState: true, 
        })
      }, 300),
    },
  },

  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },

    toggleExportDropdown() {
      this.isExportOpen = !this.isExportOpen
    },

    handleOutsideClick(event) {
      if (this.isExportOpen && this.$refs.exportDropdownRef && !this.$refs.exportDropdownRef.contains(event.target)) {
        this.isExportOpen = false
      }
    },

    openImportModal() {
      this.isImportModalOpen = true
      this.resetImportFile()
    },

    closeImportModal() {
      if (this.isImporting) return
      this.isImportModalOpen = false
      this.resetImportFile()
    },

    resetImportFile() {
      this.importStep = 'upload'
      this.selectedFile = null
      this.selectedFileName = ''
      this.isPreviewLoading = false
      this.isImporting = false
      this.previewError = ''
      this.previewData = {
        total: 0,
        newCount: 0,
        updateCount: 0,
        rows: [],
      }
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    handleFileSelected(event) {
      const file = event.target.files[0]
      if (file) {
        this.processFile(file)
      }
    },

    handleFileDrop(event) {
      this.isDragging = false
      const file = event.dataTransfer.files[0]
      if (file) {
        this.processFile(file)
      }
    },

    async processFile(file) {
      this.selectedFile = file
      this.selectedFileName = file.name
      this.isPreviewLoading = true
      this.previewError = ''

      const formData = new FormData()
      formData.append('file', file)

      try {
        const response = await axios.post('/clients/import/preview', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-CSRF-TOKEN': this.csrf || document.querySelector('meta[name="csrf-token"]')?.getAttribute('content'),
          },
        })

        if (response.data.success) {
          if (response.data.total === 0) {
            this.previewError = 'El archivo está vacío o no contiene filas válidas de clientes.'
            this.isPreviewLoading = false
            return
          }
          this.previewData = response.data
          this.importStep = 'preview'
        } else {
          this.previewError = response.data.message || 'Error al procesar el archivo.'
        }
      } catch (error) {
        this.previewError = error.response?.data?.message || 'Error al conectar con el servidor para previsualizar.'
      } finally {
        this.isPreviewLoading = false
      }
    },

    async submitImport() {
      if (!this.selectedFile) return
      this.isImporting = true
      this.previewError = ''

      const formData = new FormData()
      formData.append('file', this.selectedFile)

      try {
        const response = await axios.post('/clients/import', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Accept': 'application/json',
            'X-CSRF-TOKEN': this.csrf || document.querySelector('meta[name="csrf-token"]')?.getAttribute('content'),
          },
        })

        if (response.data.success) {
          this.closeImportModal()
          
          const alertHelper = this.$swal || Swal
          if (alertHelper) {
            alertHelper.fire({
              title: '¡Importación Exitosa!',
              text: response.data.message || 'Los clientes han sido importados correctamente.',
              icon: 'success',
              confirmButtonColor: '#f97316',
              confirmButtonText: 'Aceptar',
            })
          }

          this.$inertia.reload({ preserveScroll: true })
        } else {
          this.previewError = response.data.message || 'Ocurrió un error al importar los clientes.'
        }
      } catch (error) {
        this.previewError = error.response?.data?.message || 'Error inesperado al emitir la importación.'
      } finally {
        this.isImporting = false
      }
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value)
    },

    deleteClient(client) {
      if (confirm('¿Está seguro que desea eliminar este cliente?')) {
        this.$inertia.delete(`/clients/${client.id}`)
      }
    },
  },
}
</script>
